import { defineStore } from 'pinia';
import axios from 'axios';
import axiosInstance from '@/services/axiosInstance';
import { IUser } from '@/types';
import { useAppStore } from '@/stores/app/appStore';
import i18n from '@/plugins/i18n';
import { subscribeToPushNotifications } from '@/composables/usePushNotifications';

const { t } = i18n.global;

// Helper pro dekódování JWT tokenu
function decodeJwtPayload(token: string): { _id?: string; id?: string; exp?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}



const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export const useUserStore = defineStore('userStore', {
  state: () => ({
    token: null as string | null,
    user: null as IUser | null,
    subscription: null as PushSubscription | null,
    eventRecords: [] as { id: string; playByPlay: any[] }[],
  }),
  actions: {
    async setUser(data: { user: IUser, token: string }) {
      this.user = data.user;
      this.token = data.token;
      localStorage.setItem('token', data.token);
      if (this.user?._id) {
        await subscribeToPushNotifications(this.user._id);
      }
      const appStore = useAppStore();
            appStore.triggerRefetch();
    },
    setToken(newToken: string) {
      this.token = newToken;
      localStorage.setItem('token', newToken);
    },

    async clearUser() {
      // 1) Uložit token pro server logout
      const savedToken = this.token;

      // 2) Vyčistit stav SYNCHRONNĚ jako první – žádný interceptor už nepřepíše token zpět
      this.user = null;
      this.token = null;
      this.subscription = null;
      localStorage.removeItem('token');

      // 3) Notifikovat server (plain axios bez interceptorů, fire-and-forget)
      if (savedToken) {
        try {
          const baseURL = axiosInstance.defaults.baseURL;
          await axios.post(`${baseURL}/auth/logout`, {}, {
            headers: { Authorization: `Bearer ${savedToken}` },
            withCredentials: true,
          });
        } catch (err) {
          console.warn('Logout endpoint selhal, pokračuji lokálně', err);
        }
      }
    },
    
    async loadUserFromStorage() {
      const token = localStorage.getItem('token');
      //console.log('Načítám uživatele z localStorage, token:', token);
      if (token) {
        // Dekódujeme token pro získání userId před nastavením do store
        const payload = decodeJwtPayload(token);
        const userId = payload?._id || payload?.id;
        if (!userId) {
          console.warn('Token neobsahuje platné userId, odstraňuji token');
          localStorage.removeItem('token');
          return;
        }
        // Kontrola expirace tokenu
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          console.warn('Token vypršel, pokus o obnovení...');
          try {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {}, {
              withCredentials: true
            });
            const newToken = response.data.token;
            this.setToken(newToken);
            
            const newPayload = decodeJwtPayload(newToken);
            const newUserId = newPayload?._id || newPayload?.id;
            if (newUserId) {
              await this.fetchUserData(newUserId);
            }
            return;
          } catch (err) {
            console.warn('Obnovení tokenu selhalo, odstraňuji token', err);
            localStorage.removeItem('token');
            const appStore = useAppStore();
            // appStore.showSessionExpired = true;
            return;
          }
        }
        this.token = token;
        await this.fetchUserData(userId);
        // Re-register push notifications (listeners are guarded against duplicates)
        if (this.user?._id) {
          await subscribeToPushNotifications(this.user._id);
        }
      }
    },
    async fetchUserData(userIdParam?: string) {
      try {
        //console.log('Načítám uživatelská data');
        // Použij předaný parametr, nebo fallback na getter (pro volání z jiných míst)
        const userId = userIdParam || this.getUserId;
        if (!userId) {
          console.warn('User ID is not available, skipping fetchUserData');
          return;
        }
        const response = await axiosInstance.get(`${API_BASE_URL}/users/${userId}`);
        this.user = response.data;
      } catch (error: any) {
        console.error('Chyba při načítání uživatelských dat:', error);
        // Odhlášení pouze při 401/403 chybách, ne při síťových problémech
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          this.clearUser();
        }
      }
    },


    async clearEventRecordCache(eventId: string) {
      const before = this.eventRecords.length;
      // smaž celý záznam pro daný event
      this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
      const removed = before !== this.eventRecords.length;
      return removed; // můžeš využít v UI pro feedback
    },

    async clearEventRecordEntries(eventId: string) {
      const rec = this.eventRecords.find(er => er.id === eventId);
      if (rec) rec.playByPlay = [];
      return !!rec;
    },

    async updateGameStatus(eventId: string, status: any) {
      console.log('updateGameStatus called with:', eventId, status);
      try {
        const appStore = useAppStore();
        appStore.showLoadingOverlay(t('updateGameStatus'));
        await axiosInstance.put(`${API_BASE_URL}/events/${eventId}/gamestatus`, status);
        //console.log('Stav hry byl úspěšně aktualizován na serveru.', status);
      } catch (error) {
        console.error('Chyba při aktualizaci stavu hry na server:', error);
        // případně toast/snackbar
      } finally {
        const appStore = useAppStore();
        appStore.closeLoadingOverlay();
      }
        
    },

    // Upravená verze addEventRecord:
    async addEventRecord(eventId: string, record: any) {

      // 1) Najdi nebo vytvoř lokální eventRecord
      let eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        eventRecord = { id: eventId, playByPlay: [] };
        this.eventRecords.push(eventRecord);
      }

      // 2) Deduplikace pro break záznamy (v split režimu musí porovnávat i _partIndex)
      if (record.type === 'break') {
        const exists = eventRecord.playByPlay.some(er =>
          er?.data?.breakType === record?.data?.breakType &&
          er?.data?.partNumber === record?.data?.partNumber &&
          (er._partIndex ?? 0) === (record._partIndex ?? 0)
        );
        if (exists) return;
      }

      try {
        const appStore = useAppStore();
        appStore.showLoadingOverlay(t('addingEventRecord'));

        // 3) Sync na server
        await axiosInstance.post(
          `${API_BASE_URL}/events/${eventId}/playbyplayrecord`,
          record
        );

        // 4) Lokální přidání a setřídění
        eventRecord.playByPlay.push(record);
        eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));

      } catch (error) {
        console.error('Chyba při přidávání play-by-play záznamu na server:', error);

        // gameEnd záznamy nepřidáváme lokálně při selhání serveru
        // (server je odmítne pokud už existují, phantom záznam by zmátl UI)
        const isGameEnd = record.type === 'break' && record.data?.breakType === 'gameEnd';
        if (!isGameEnd) {
          record.addSyncErr = true;
          eventRecord.playByPlay.push(record);
          eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));
        }
      } finally {
        const appStore = useAppStore();
        appStore.closeLoadingOverlay();
      }
    },



    // Upravená verze removeEventRecord:
    async removeEventRecord(eventId: string, payload: { recordId: string, forceLocal: boolean }) {
      const eventRecord = this.eventRecords.find(er => er.id === eventId);


      if (eventRecord) {
        // Najdeme konkrétní záznam
        const record = eventRecord.playByPlay.find(r => r.id === payload.recordId);
        try {
          // Pokus o synchronizaci odebrání na backendu (endpoint: POST /events/:id/playbyplayrecord/:recordId)
          await axiosInstance.delete(`${API_BASE_URL}/events/${eventId}/playbyplayrecord/${payload.recordId}`);
          // Odebrání záznamu ze store při úspěšné synchronizaci
          eventRecord.playByPlay = eventRecord.playByPlay.filter(r => r.id !== payload.recordId);
          if (eventRecord.playByPlay.length === 0) {
            this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
          }
        } catch (error: any) {
          // Re-throw PLAYOFF_IN_PROGRESS so callers can show a dialog
          if (error?.response?.status === 403 && error?.response?.data?.code === 'PLAYOFF_IN_PROGRESS') {
            throw error;
          }
          console.error('Chyba při odebírání play-by-play záznamu na server:', error);
          // Při selhání aktualizujeme záznam s flagem, že se odstranění synchronizovat nepovedlo
          if (record) {
            record.deleteSyncErr = true;
          }
          if (payload.forceLocal) {
            eventRecord.playByPlay = eventRecord.playByPlay.filter(r => r.id !== payload.recordId);
            if (eventRecord.playByPlay.length === 0) {
              this.eventRecords = this.eventRecords.filter(er => er.id !== eventId);
            }
          }
        }
      }
    },

    // Nová metoda playByPlayResend:
    async playByPlayResend(eventId: string, recordId: string) {
      // Vyhledání eventRecord ve store podle eventId
      const eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        console.error(`Event record pro id ${eventId} nebyl nalezen.`);
        return;
      }

      // Vyhledání konkrétního záznamu podle recordId
      const record = eventRecord.playByPlay.find(r => r.id === recordId);
      if (!record) {
        console.error(`Záznam s recordId ${recordId} nebyl nalezen v event record.`);
        return;
      }

      try {
        // Pokus o opětovné odeslání záznamu na backend
        await axiosInstance.post(`${API_BASE_URL}/events/${eventId}/playbyplayrecord`, record);
        // Pokud bylo odeslání úspěšné, odstraníme flag synchronizační chyby, pokud existuje
        if (record.addSyncErr) {
          delete record.addSyncErr;
        }
        //console.log('Play-by-play záznam byl úspěšně odeslán znovu.');
      } catch (error) {
        console.error('Chyba při odesílání play-by-play záznamu na server:', error);
        // Nastavíme flag, že synchronizace se opět nezdařila
        record.addSyncErr = true;
      }
    },

    // Nová metoda v userStore pro synchronizaci play-by-play záznamů:
    // Nová metoda v userStore pro synchronizaci play-by-play záznamů:
    async syncEventRecords(eventId: string, serverRecords: any[]) {
      // 0) Normalizace vstupu - i kdyby přišlo null/undefined/nepole
      const srv = Array.isArray(serverRecords) ? serverRecords : [];

      // 1) Najdi / vytvoř lokální eventRecord
      let eventRecord = this.eventRecords.find(er => er.id === eventId);
      if (!eventRecord) {
        this.eventRecords.push({
          id: eventId,
          playByPlay: srv.slice(), // klon
        });
        return;
      }
      if (!Array.isArray(eventRecord.playByPlay)) {
        eventRecord.playByPlay = [];
      }

      // 2) Mapa serverových záznamů podle id
      const serverRecordsMap = new Map<string, any>();
      for (const sr of srv) {
        if (sr && sr.id) serverRecordsMap.set(String(sr.id), sr);
      }

      // 3) Sloučení: lokální → server
      for (const localRecord of eventRecord.playByPlay) {
        const lid = localRecord?.id ? String(localRecord.id) : undefined;

        if (lid && serverRecordsMap.has(lid)) {
          // existuje i na serveru → přepiš lokální daty ze serveru
          Object.assign(localRecord, serverRecordsMap.get(lid));
          delete localRecord.addSyncErr;
          serverRecordsMap.delete(lid);
        } else {
          // neznáme serverovou verzi nebo lokální bez id → označ sync chybu
          localRecord.addSyncErr = true;
        }
      }

      // 3.5) Odstraň phantom gameEnd záznamy (server je odmítl, lokálně zůstaly)
      eventRecord.playByPlay = eventRecord.playByPlay.filter((r: any) => {
        if (r.addSyncErr && r.type === 'break' && r.data?.breakType === 'gameEnd') return false;
        return true;
      });

      // 4) Zbylé serverové záznamy přidej
      for (const record of serverRecordsMap.values()) {
        eventRecord.playByPlay.push(record);
      }

      // 5) Seřadit bezpečně podle rawTime (s fallbackem)
      eventRecord.playByPlay.sort((a, b) => (b?.rawTime ?? 0) - (a?.rawTime ?? 0));
    },


    // uvnitř actions: { ... }
    hasRole(admins?: Array<string | { _id?: string } | any>): boolean {
      const me = this.user?._id;
      if (!me) return false;

      // 1) globální admin
      const roles: string[] = Array.isArray((this.user as any)?.roles) ? (this.user as any).roles : [];
      if (roles.includes('administrator')) return true;

      // 2) je v seznamu admins (string / {_id} / ObjectId)
      if (!Array.isArray(admins)) return false;

      const myId = String(me);
      return admins.some((a) => {
        if (!a) return false;
        if (typeof a === 'string') return a === myId;

        // {_id: '...'} nebo cokoliv co má _id
        if (typeof a === 'object' && a._id) return String(a._id) === myId;

        // Mongoose ObjectId apod.
        try { return String(a) === myId; } catch { return false; }
      });
    },
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    getUserName: (state) => state.user?.name || '',
    getUserToken: (state) => state.token || '',
    getUserId: (state) => state.user?._id || '',
    // Parametrizovaný getter – vrátí eventRecord podle jeho id
    getEventRecordById: (state) => {
      return (id: string) => state.eventRecords.find(record => record.id === id);
    },
  },
  // pinia-plugin-persistedstate v4 syntax
  persist: {
    key: 'userStore',
    storage: localStorage,
    pick: ['token', 'user', 'subscription', 'eventRecords'],
  },
});
